'use client';

import { ItemModel } from '@/models/item.model';
import React, { createContext, useReducer, ReactNode, useContext } from 'react';

// Define the shape of an extra
export interface Extra {
  name: string;
  price: number;
}

// Define the shape of the cart item
export interface CartItem {
  item: ItemModel;
  quantity: number;
  selectedExtras?: Extra[]; // Add selected extras
}

// Define the shape of the cart state
interface CartState {
  items: CartItem[];
  total: number;
  loading?: boolean;
}

// Define the actions for the cart reducer
type CartAction =
  | { type: 'LOADING_START'; storage?: boolean }
  | { type: 'LOADING_END'; storage?: boolean }
  | { type: 'ADD_ITEM_BATCH'; payload: CartItem[]; storage?: boolean }
  | { type: 'ADD_ITEM'; payload: CartItem; storage?: boolean }
  | {
      type: 'INCREMENT_ITEM';
      payload: { id: string; extras?: Extra[] };
      storage?: boolean;
    }
  | {
      type: 'DECREMENT_ITEM';
      payload: { id: string; extras?: Extra[] };
      storage?: boolean;
    }
  | {
      type: 'REMOVE_ITEM';
      payload: { id: string; extras?: Extra[] };
      storage?: boolean;
    }
  | { type: 'CLEAR_CART'; storage?: boolean };

// Create the initial state
const initialState: CartState = {
  items: [],
  total: 0,
};

const saveStateToLocalStorage = (state: CartState) => {
  localStorage.setItem('cartState', JSON.stringify(state));
};

const clearLocalStorage = () => {
  localStorage.removeItem('cartState');
};

// Helper function to generate unique key for cart items
const getCartItemKey = (itemId: string, extras?: Extra[]): string => {
  if (!extras || extras.length === 0) return itemId;
  const extrasKey = extras
    .map((e) => `${e.name}:${e.price}`)
    .sort()
    .join('|');
  return `${itemId}__${extrasKey}`;
};

// Helper function to check if two extra arrays are the same
const areExtrasEqual = (extras1?: Extra[], extras2?: Extra[]): boolean => {
  if (!extras1 && !extras2) return true;
  if (!extras1 || !extras2) return false;
  if (extras1.length !== extras2.length) return false;

  const sorted1 = [...extras1].sort((a, b) => a.name.localeCompare(b.name));
  const sorted2 = [...extras2].sort((a, b) => a.name.localeCompare(b.name));

  return sorted1.every(
    (extra, i) =>
      extra.name === sorted2[i].name && extra.price === sorted2[i].price
  );
};

// Helper function to calculate item price including extras
const calculateItemPrice = (item: ItemModel, extras?: Extra[]): number => {
  const basePrice = item.price;
  const extrasPrice = extras?.reduce((sum, extra) => sum + extra.price, 0) || 0;
  return basePrice + extrasPrice;
};

// Helper function to calculate total
const calculateTotal = (items: CartItem[]): number => {
  return items.reduce((acc, cartItem) => {
    const itemPrice = calculateItemPrice(
      cartItem.item,
      cartItem.selectedExtras
    );
    return acc + cartItem.quantity * itemPrice;
  }, 0);
};

// Create the cart reducer
const cartReducer = (state: CartState, action: CartAction): CartState => {
  let newState: CartState | undefined = undefined;

  switch (action.type) {
    case 'LOADING_START':
      newState = {
        ...state,
        loading: true,
      };
      break;
    case 'LOADING_END':
      newState = {
        ...state,
        loading: false,
      };
      break;
    case 'ADD_ITEM_BATCH':
      newState = {
        ...state,
        items: action.payload,
        total: calculateTotal(action.payload),
      };
      break;

    case 'ADD_ITEM':
      const existingItemIndex = state.items.findIndex(
        (cartItem) =>
          cartItem.item._id === action.payload.item._id &&
          areExtrasEqual(cartItem.selectedExtras, action.payload.selectedExtras)
      );
      let updatedItems = [...state.items];
      if (existingItemIndex >= 0) {
        updatedItems[existingItemIndex].quantity += +action.payload.quantity;
      } else {
        updatedItems.push(action.payload);
      }

      newState = {
        ...state,
        items: updatedItems,
        total: calculateTotal(updatedItems),
      };
      break;

    case 'REMOVE_ITEM':
      const filteredItems = state.items.filter(
        (cartItem) =>
          !(
            cartItem.item._id === action.payload.id &&
            areExtrasEqual(cartItem.selectedExtras, action.payload.extras)
          )
      );
      newState = {
        ...state,
        items: filteredItems,
        total: calculateTotal(filteredItems),
      };
      break;

    case 'DECREMENT_ITEM':
      const decrementedItemIndex = state.items.findIndex(
        (cartItem) =>
          cartItem.item._id === action.payload.id &&
          areExtrasEqual(cartItem.selectedExtras, action.payload.extras)
      );
      if (decrementedItemIndex === -1) return state;

      const decrementedItems = [...state.items];
      decrementedItems[decrementedItemIndex].quantity -= 1;

      const finalDecrementedItems =
        decrementedItems[decrementedItemIndex].quantity <= 0
          ? decrementedItems.filter((_, i) => i !== decrementedItemIndex)
          : decrementedItems;

      newState = {
        ...state,
        items: finalDecrementedItems,
        total: calculateTotal(finalDecrementedItems),
      };
      break;

    case 'INCREMENT_ITEM':
      const incrementedItemIndex = state.items.findIndex(
        (cartItem) =>
          cartItem.item._id === action.payload.id &&
          areExtrasEqual(cartItem.selectedExtras, action.payload.extras)
      );
      if (incrementedItemIndex === -1) return state;

      const incrementedItems = [...state.items];
      incrementedItems[incrementedItemIndex].quantity += 1;

      newState = {
        ...state,
        items: incrementedItems,
        total: calculateTotal(incrementedItems),
      };
      break;

    case 'CLEAR_CART':
      newState = initialState;
      if (action.storage !== false) clearLocalStorage();
      break;

    default:
      return state;
  }

  if (action.type !== 'CLEAR_CART' && action.storage !== false) {
    saveStateToLocalStorage(newState);
  }

  return newState;
};

const CartContext = createContext<{
  state: CartState;
  dispatch: React.Dispatch<CartAction>;
  loadOrderFromStorage: () => void;
}>({
  state: initialState,
  dispatch: () => null,
  loadOrderFromStorage: () => {},
});

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  const loadOrderFromStorage = () => {
    const savedState = localStorage.getItem('cartState');
    if (savedState) {
      console.log('Loading cart state from local storage');
      const parsedState = JSON.parse(savedState);
      dispatch({ type: 'ADD_ITEM_BATCH', payload: parsedState.items });
    }
  };

  return (
    <CartContext.Provider value={{ state, dispatch, loadOrderFromStorage }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  return useContext(CartContext);
};
